<template>
  <v-container fluid class="pa-0">
    <!-- Si no hay un ciclo cargado, mostrar alerta -->
    <v-row v-if="!cicloActivo || !cicloActivo.id">
      <v-col cols="12" class="text-center py-10">
        <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <h3 class="text-h6 grey--text text--darken-2 mt-4">No hay un Ciclo Activo seleccionado</h3>
        <p class="grey--text">Para gestionar proyectos offline, primero debes configurar un ciclo presupuestal.</p>
        <v-btn color="primary" outlined @click="cargarCicloActivo" :loading="cargando">
          <v-icon left>mdi-refresh</v-icon> Intentar Cargar Ciclo Reciente
        </v-btn>
      </v-col>
    </v-row>

    <!-- Content (if cycle loaded) -->
    <div v-else>
      <v-card outlined class="rounded-lg pa-4 mb-4 bg-light-blue border-primary">
        <v-row align="center">
          <v-col cols="12" md="4" class="py-1">
            <span class="text-caption grey--text text--darken-1 text-uppercase font-weight-bold">Presupuesto Físico Extra</span>
            <div class="text-subtitle-1 font-weight-bold primary--text">
              <v-icon color="indigo" small class="mr-1">mdi-storefront</v-icon> Gestión de Capex ({{ instancia }})
            </div>
          </v-col>
          <v-col cols="12" md="4" class="py-1">
            <span class="text-caption grey--text text--darken-1 text-uppercase font-weight-bold">Total Solicitado</span>
            <div class="text-subtitle-1 font-weight-bold orange--text text--darken-2">
              {{ totalSolicitado | currency }}
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right py-1">
            <v-btn color="primary" @click="abrirModalNuevo" :disabled="!isDirector">
              <v-icon left>mdi-plus</v-icon> Nuevo Proyecto
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Tabla de Proyectos -->
      <v-card outlined class="rounded-lg">
        <v-data-table
          :headers="headers"
          :items="proyectos"
          :loading="cargando"
          class="elevation-0 table-rounded"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:item.estado="{ item }">
            <v-chip :color="getColorEstado(item.estado)" dark small label>
              {{ item.estado }}
            </v-chip>
          </template>
          <template v-slot:item.monto_solicitado="{ item }">
            <span class="font-weight-medium">{{ item.monto_solicitado | currency }}</span>
          </template>
          <template v-slot:item.monto_ejecutado="{ item }">
            <span class="font-weight-bold" :class="item.monto_ejecutado > 0 ? 'success--text' : 'grey--text'">
              {{ item.monto_ejecutado | currency }}
            </span>
          </template>
          <template v-slot:item.acciones="{ item }">
            <v-btn icon small color="primary" @click="abrirModalEdicion(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Modal Crear/Editar Proyecto -->
      <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card class="rounded-lg">
          <v-card-title class="bg-light-grey text-h6 border-bottom">
            {{ isEditing ? 'Revisar Proyecto' : 'Nuevo Proyecto Offline' }}
            <v-spacer></v-spacer>
            <v-btn icon @click="cerrarModal"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          
          <v-card-text class="pt-4">
            <v-form ref="form" v-model="formValid" lazy-validation>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="form.sucursal"
                    :items="planteles"
                    item-text="plantel"
                    item-value="plantel"
                    label="Sucursal / Sede"
                    outlined dense
                    :rules="[v => !!v || 'La sucursal es obligatoria']"
                    :disabled="bloquearEdicionBorrador"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="form.responsable"
                    :items="usuarios"
                    item-text="nombre_completo"
                    item-value="nombre_completo"
                    label="Responsable"
                    outlined dense
                    :rules="[v => !!v || 'El responsable es obligatorio']"
                    :disabled="bloquearEdicionBorrador"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.monto_solicitado"
                    label="Monto Solicitado ($)"
                    type="number"
                    outlined dense
                    prefix="$"
                    :rules="[v => v > 0 || 'Debe ser mayor a 0']"
                    :disabled="bloquearEdicionBorrador"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.estado"
                    :items="estadosList"
                    label="Estado del Proyecto"
                    outlined dense
                    :disabled="!isDirector"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.descripcion"
                    label="Descripción (Pintura, Volantes, Evento...)"
                    outlined dense rows="3"
                    :disabled="bloquearEdicionBorrador"
                  ></v-textarea>
                </v-col>

                <!-- Campos Exclusivos si se Aprueba/Ejecuta -->
                <v-col cols="12" v-if="form.estado === 'Autorizado' || form.estado === 'Ejecutado'">
                  <v-alert type="warning" dense outlined class="text-caption mb-4" v-if="isDirector">
                    Para autorizar este proyecto, Finanzas debe haber emitido un folio válido.
                  </v-alert>
                  <v-text-field
                    v-model="form.referencia_finanzas"
                    label="Referencia o Folio de Finanzas (Requerido para Autorizar)"
                    outlined dense
                    :rules="[v => !!v || 'Referencia obligatoria para autorizar']"
                    :disabled="bloquearEdicionBorrador"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6" v-if="form.estado === 'Autorizado' || form.estado === 'Ejecutado'">
                  <v-text-field
                    v-model.number="form.monto_ejecutado"
                    label="Monto Real Ejecutado ($)"
                    type="number"
                    outlined dense
                    prefix="$"
                    :disabled="form.estado === 'Ejecutado' && !isDirector"
                    persistent-hint
                    hint="Al capturar el monto real gastado, el proyecto pasará automáticamente a Ejecutado."
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          
          <v-divider></v-divider>
          <v-card-actions class="pa-4 bg-light-grey">
            <v-spacer></v-spacer>
            <v-btn text @click="cerrarModal" :disabled="guardando">Cancelar</v-btn>
            <v-btn color="primary" @click="guardarProyecto" :loading="guardando" :disabled="!formValid">
              <v-icon left>mdi-check</v-icon> {{ isEditing ? 'Guardar Cambios' : 'Solicitar Presupuesto' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>
  </v-container>
</template>

<script>
export default {
  name: 'TabProyectosOffline',
  props: {
    instancia: {
      type: String,
      required: true
    },
    isDirector: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      cargando: false,
      cicloActivo: null,
      proyectos: [],
      planteles: [],
      usuarios: [],
      
      dialog: false,
      isEditing: false,
      guardando: false,
      formValid: true,
      
      form: {
        id: null,
        sucursal: '',
        responsable: '',
        descripcion: '',
        monto_solicitado: 0,
        monto_ejecutado: 0,
        estado: 'Pendiente',
        referencia_finanzas: ''
      },

      estadosList: ['Pendiente', 'Autorizado', 'Ejecutado', 'Rechazado'],

      headers: [
        { text: 'Sucursal', value: 'sucursal', class: 'font-weight-bold' },
        { text: 'Responsable', value: 'responsable' },
        { text: 'Descripción', value: 'descripcion', width: '30%' },
        { text: 'Solicitado', value: 'monto_solicitado', align: 'right' },
        { text: 'Ejecutado', value: 'monto_ejecutado', align: 'right' },
        { text: 'Estado', value: 'estado', align: 'center' },
        { text: 'Acciones', value: 'acciones', sortable: false, align: 'center' }
      ]
    };
  },

  computed: {
    totalSolicitado() {
      return this.proyectos.reduce((acc, curr) => acc + (parseFloat(curr.monto_solicitado) || 0), 0);
    },
    // isDirector viene del prop (propagado desde PanelPresupuestos.vue)
    // Si es true, el usuario puede editar estados y aprobar proyectos
    bloquearEdicionBorrador() {
      return this.isEditing && this.form.estado !== 'Pendiente' && !this.isDirector;
    }
  },

  mounted() {
    this.cargarCatálogos();
    this.cargarCicloActivo();
  },

  methods: {
    async cargarCatálogos() {
      try {
         const resPlanteles = await this.$http.get('planteles.all');
         this.planteles = resPlanteles.data ? resPlanteles.data.filter(el => el.idplantel != 0) : [];
         
         const resUsuarios = await this.$http.get('usuarios.all.erp');
         if (resUsuarios.data) {
             resUsuarios.data.forEach(u => u.nombre_completo = u.nombre_completo ? u.nombre_completo.toUpperCase() : '');
             this.usuarios = resUsuarios.data;
         }
      } catch (e) {
         console.warn("Fallo carga catálogos", e);
      }
    },
    async cargarCicloActivo() {
      this.cargando = true;
      try {
        const resCiclos = await this.$http.get(`api/presupuestos/ciclos/${this.instancia}`);
        if (resCiclos.data && resCiclos.data.length > 0) {
          this.cicloActivo = resCiclos.data[0];
          await this.cargarProyectos();
        } else {
          this.cicloActivo = null;
        }
      } catch (error) {
        console.error("Error cargando ciclo", error);
      } finally {
        this.cargando = false;
      }
    },

    async cargarProyectos() {
      if(!this.cicloActivo) return;
      try {
        const res = await this.$http.get(`api/presupuestos/proyectos/${this.cicloActivo.id}/${this.instancia}`);
        this.proyectos = res.data || [];
      } catch (error) {
        console.error("Error cargando proyectos", error);
      }
    },

    abrirModalNuevo() {
      this.isEditing = false;
      this.form = {
        id: null,
        sucursal: '',
        responsable: '',
        descripcion: '',
        monto_solicitado: 0,
        monto_ejecutado: 0,
        estado: 'Pendiente',
        referencia_finanzas: ''
      };
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.dialog = true;
    },

    abrirModalEdicion(item) {
      this.isEditing = true;
      this.form = { ...item };
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.dialog = true;
    },

    cerrarModal() {
      this.dialog = false;
    },

    async guardarProyecto() {
      if (!this.$refs.form.validate()) return;
      
      this.guardando = true;
      try {
        // Auto-promoción a Ejecutado si capturan monto ejecutado y estaba en Autorizado.
        if (this.form.estado === 'Autorizado' && this.form.monto_ejecutado > 0) {
            this.form.estado = 'Ejecutado';
        }

        const payload = {
          ...this.form,
          id_presupuesto_ciclo: this.cicloActivo.id,
          instancia: this.instancia
        };

        if (this.isEditing) {
          await this.$http.put(`api/presupuestos/proyectos/${this.form.id}`, payload);
        } else {
          // Asumir un usuario generico si no está inyectado
          payload.usuario_registro = 1; 
          await this.$http.post(`api/presupuestos/proyectos`, payload);
        }
        
        this.cerrarModal();
        await this.cargarProyectos();
        console.log("Proyecto guardado");

      } catch (error) {
        console.error("Error guardando proyecto", error);
      } finally {
        this.guardando = false;
      }
    },

    getColorEstado(estado) {
      const colors = {
        'Pendiente': 'orange',
        'Autorizado': 'blue',
        'Ejecutado': 'success',
        'Rechazado': 'error'
      };
      return colors[estado] || 'grey';
    }
  },

  filters: {
    currency(value) {
      if (typeof value !== "number") return value;
      return "$" + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
};
</script>

<style scoped>
.bg-light-blue {
  background-color: #F1F8FF;
}
.bg-light-grey {
  background-color: #fcfcfc;
}
.border-primary {
  border: 1px solid var(--v-primary-base) !important;
}
.border-bottom {
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.table-rounded {
  border-radius: 8px;
  overflow: hidden;
}
</style>
